import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  getAIRecordById,
  addDoctorReport,
} from "../../../services/oprations/aiRecordAPI";
import { useSelector } from "react-redux";

const PatientDetails = () => {
  const { darkMode } = useOutletContext(); // ✅ Dark mode context
  const { user } = useSelector((state) => state.profile);
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [doctorReportInput, setDoctorReportInput] = useState("");
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const data = await getAIRecordById(id);
        setPatient(data);
      } catch (error) {
        console.error("Failed to fetch patient:", error);
      }
    };
    fetchPatient();
  }, [id]);

  const stripMarkdown = (text) => {
    if (!text) return "";
    return text
      .replace(/(^#+\s*)/gm, "")
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      .trim();
  };

  const firstConsultation = patient?.consultations?.[0];
  let patientName =
    firstConsultation?.markdown?.split("\n")[2]?.replace("- Name: ", "") ||
    "Unknown";
  patientName = patientName.replace(/^#+\s*/, "");

  const toggleConsultation = (c) => {
    if (selectedConsultation?._id === c._id) {
      setSelectedConsultation(null);
      setDoctorReportInput("");
    } else {
      setSelectedConsultation(c);
      setDoctorReportInput(c.doctor_report || "");
    }
  };

  const handleSaveDoctorReport = async () => {
    if (!doctorReportInput.trim()) return;
    setLoadingReport(true);
    try {
      await addDoctorReport({
        patient_id: patient.patient_id,
        consultation_id: selectedConsultation._id,
        doctor_report: doctorReportInput,
      });
      setPatient((prev) => {
        const updatedConsultations = prev.consultations.map((c) =>
          c._id === selectedConsultation._id
            ? { ...c, doctor_report: doctorReportInput }
            : c
        );
        return { ...prev, consultations: updatedConsultations };
      });
      setSelectedConsultation((prev) => ({
        ...prev,
        doctor_report: doctorReportInput,
      }));
    } catch (err) {
      console.error("Failed to update doctor report:", err);
    } finally {
      setLoadingReport(false);
    }
  };

  if (!patient) {
    return (
      <div className="p-6">
        <p className="text-indigo-500 animate-pulse">Loading patient data...</p>
      </div>
    );
  }

  return (
    <div
      className={`p-6 space-y-6 transition-colors duration-300 ${
        darkMode ? " text-gray-100" : "bg-gray-50 text-gray-800"
      }`}
    >
      <button
        onClick={() => navigate(-1)}
        className={`flex items-center gap-2 font-medium transition-colors ${
          darkMode
            ? "text-indigo-400 hover:text-indigo-300"
            : "text-indigo-600 hover:text-indigo-800"
        }`}
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div
        className={`rounded-xl p-6 shadow-sm border transition-colors duration-300 ${
          darkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <h2 className="text-2xl font-bold mb-2">{patientName}</h2>
        <p
          className={`mb-4 ${
            darkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Patient ID: {patient.patient_id}
        </p>

        <h3 className="text-lg font-semibold mb-2">Consultations</h3>
        <ul className="space-y-2">
          {patient.consultations.map((c) => (
            <li
              key={c._id}
              onClick={() => toggleConsultation(c)}
              className={`p-3 rounded-lg cursor-pointer border transition-colors duration-200 ${
                darkMode
                  ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                  : "bg-gray-50 border-gray-200 hover:bg-indigo-50"
              }`}
            >
              Consultation {c.consultation_number} –{" "}
              {new Date(c.timestamp).toLocaleDateString()}
            </li>
          ))}
        </ul>

        {selectedConsultation && (
          <div
            className={`mt-6 p-4 border rounded-lg space-y-4 transition-colors duration-200 ${
              darkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-gray-50 border-gray-300"
            }`}
          >
            <h4 className="text-lg font-semibold">Consultation Details</h4>
            <p
              className={`whitespace-pre-wrap ${
                darkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              {stripMarkdown(selectedConsultation.markdown)}
            </p>

            <div>
              <p
                className={`font-semibold mt-2 ${
                  darkMode ? "text-gray-100" : "text-gray-800"
                }`}
              >
                Doctor Report:
              </p>

              {selectedConsultation.doctor_report ? (
                <p
                  className={`whitespace-pre-wrap ${
                    darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {stripMarkdown(selectedConsultation.doctor_report)}
                </p>
              ) : user.role === "Doctor" ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    rows={4}
                    placeholder="Add doctor report..."
                    value={doctorReportInput}
                    onChange={(e) => setDoctorReportInput(e.target.value)}
                    className={`w-full p-2 rounded-lg border transition-colors duration-200 ${
                      darkMode
                        ? "bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-800 placeholder-gray-400"
                    }`}
                  />
                  <button
                    onClick={handleSaveDoctorReport}
                    disabled={loadingReport}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      darkMode
                        ? "bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                    }`}
                  >
                    {loadingReport ? "Saving..." : "Save Report"}
                  </button>
                </div>
              ) : (
                <p className={darkMode ? "text-gray-400" : "text-gray-500"}>
                  No Report Available
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientDetails;
